import { httpClient } from '@/shared/api';
import {
  toCalcBandSchoolCountPostBody,
  type CalcBandSchoolCountItem,
  type CalcBandSchoolCountResponse,
  type SchoolBandDto,
  type SchoolBandsPagedResult,
  type SchoolByIdsDto,
  type SchoolByIdsPagedResult,
} from './schoolBands.dto';

const PREFIX = '/app/school-bands';

function mapSchoolIdsRecord(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) out[k] = n;
  }
  return Object.keys(out).length ? out : undefined;
}

/** Normalize one school-band row from calc or list (camelCase / PascalCase). */
function mapSchoolBandFromRaw(raw: unknown): SchoolBandDto {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '',
      minValue: 0,
      maxValue: 0,
      minSchoolsPerBand: 0,
      year: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  const sid = o.schoolIds ?? o.SchoolIds;
  const schoolIds = mapSchoolIdsRecord(sid);
  const rawSchools = o.schools ?? o.Schools;
  const schools = Array.isArray(rawSchools)
    ? rawSchools.map(mapSchoolByIdsItem).filter((s) => Boolean(s.id?.trim()))
    : undefined;
  return {
    id: String(o.id ?? o.Id ?? ''),
    creationTime: o.creationTime as string | undefined,
    creatorId: (o.creatorId ?? o.CreatorId) as string | null | undefined,
    lastModificationTime: o.lastModificationTime as string | undefined,
    lastModifierId: (o.lastModifierId ?? o.LastModifierId) as string | null | undefined,
    isDeleted: o.isDeleted as boolean | undefined,
    deleterId: (o.deleterId ?? o.DeleterId) as string | null | undefined,
    deletionTime: o.deletionTime as string | undefined,
    name: (o.name ?? o.Name) as string | null | undefined,
    fullName: (o.fullName ?? o.FullName) as string | null | undefined,
    minValue: Number(o.minValue ?? o.MinValue ?? 0),
    maxValue: Number(o.maxValue ?? o.MaxValue ?? 0),
    description: (o.description ?? o.Description) as string | null | undefined,
    minSchoolsPerBand: Number(o.minSchoolsPerBand ?? o.MinSchoolsPerBand ?? 0),
    year: Number(o.year ?? o.Year ?? 0),
    numberOfSchools: Number(o.numberOfSchools ?? o.NumberOfSchools ?? 0),
    schoolIds,
    schools: schools?.length ? schools : undefined,
    totalSchoolOutOfBands: (() => {
      const v = o.totalSchoolOutOfBands ?? o.TotalSchoolOutOfBands;
      return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
    })(),
  };
}

function normalizeCalcBandSchoolCountResponse(raw: unknown): CalcBandSchoolCountResponse {
  if (!raw || typeof raw !== 'object') {
    return { bands: [], totalSchoolOutOfBands: 0, totalSchoolOutOfBandsIds: [] };
  }
  const o = raw as Record<string, unknown>;
  const rawBands = (o.bands ?? o.Bands) as unknown[] | undefined;
  const bands = Array.isArray(rawBands) ? rawBands.map(mapSchoolBandFromRaw) : [];
  const tsob = o.totalSchoolOutOfBands ?? o.TotalSchoolOutOfBands;
  const totalSchoolOutOfBands =
    typeof tsob === 'number' && Number.isFinite(tsob) ? tsob : 0;
  const rawIds = (o.totalSchoolOutOfBandsIds ?? o.TotalSchoolOutOfBandsIds) as unknown;
  const totalSchoolOutOfBandsIds = Array.isArray(rawIds)
    ? rawIds.map((id) => String(id)).filter((id) => id.trim())
    : [];
  return { bands, totalSchoolOutOfBands, totalSchoolOutOfBandsIds };
}

/** Normalize one school row (camelCase or PascalCase from API). */
export function mapSchoolByIdsItem(raw: unknown): SchoolByIdsDto {
  if (!raw || typeof raw !== 'object') {
    return { id: '' };
  }
  const o = raw as Record<string, unknown>;
  const sc = o.totalStudentCount ?? o.TotalStudentCount;
  return {
    id: String(o.id ?? ''),
    schoolName: (o.schoolName ?? o.SchoolName) as string | null | undefined,
    educationalCenterNumber: (o.educationalCenterNumber ?? o.EducationalCenterNumber) as
      | string
      | null
      | undefined,
    code: (o.code ?? o.Code) as string | null | undefined,
    totalStudentCount:
      typeof sc === 'number' && Number.isFinite(sc) ? sc : Number(sc) || 0,
  };
}

/** POST school-by-ids — `{ items, totalCount }` or legacy bare array. */
function normalizeSchoolByIdsResponse(raw: unknown): SchoolByIdsPagedResult {
  if (Array.isArray(raw)) {
    const items = (raw as unknown[]).map(mapSchoolByIdsItem);
    return { items, totalCount: items.length };
  }
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const rawItems = (o.items ?? o.Items) as unknown[] | undefined;
    const list = Array.isArray(rawItems) ? rawItems.map(mapSchoolByIdsItem) : [];
    const tc = o.totalCount ?? o.TotalCount;
    if (typeof tc === 'number' && Number.isFinite(tc)) {
      return { items: list, totalCount: tc };
    }
    return { items: list, totalCount: list.length };
  }
  return { items: [], totalCount: 0 };
}

function normalizeSchoolBandsPagedResult(raw: unknown): SchoolBandsPagedResult {
  if (!raw || typeof raw !== 'object') {
    return { items: [], totalCount: 0 };
  }
  const o = raw as Record<string, unknown>;
  const rawItems = (o.items ?? o.Items) as unknown[] | undefined;
  const items = Array.isArray(rawItems) ? rawItems.map(mapSchoolBandFromRaw) : [];
  const tc = o.totalCount ?? o.TotalCount;
  const totalCount =
    typeof tc === 'number' && Number.isFinite(tc) ? tc : items.length;
  return { items, totalCount };
}

function toQuery(params: {
  Year: number;
  MinSchoolsPerBand?: number;
  SkipCount?: number;
  MaxResultCount?: number;
  Sorting?: string;
  ExcludeDetails?: boolean;
}): string {
  const search = new URLSearchParams();
  search.set('Year', String(params.Year));
  if (params.MinSchoolsPerBand != null) {
    search.set('MinSchoolsPerBand', String(params.MinSchoolsPerBand));
  }
  if (params.ExcludeDetails != null) {
    search.set('ExcludeDetails', String(params.ExcludeDetails));
  }
  if (params.SkipCount != null) search.set('SkipCount', String(params.SkipCount));
  if (params.MaxResultCount != null) search.set('MaxResultCount', String(params.MaxResultCount));
  if (params.Sorting != null && params.Sorting !== '') search.set('Sorting', params.Sorting);
  const q = search.toString();
  return q ? `?${q}` : '';
}

export const schoolBandsApi = {
  /**
   * GET /api/app/school-bands — bands for an academic year (ABP paged list).
   */
  getList: async (params: {
    year: number;
    /** When set, server returns bands computed for this minimum (step 2). */
    minSchoolsPerBand?: number;
    /** When true, omits nested tuition/school details (lighter list for filters). */
    excludeDetails?: boolean;
    skipCount?: number;
    maxResultCount?: number;
    sorting?: string;
  }) => {
    const raw = await httpClient.get<unknown>(
      `${PREFIX}${toQuery({
        Year: params.year,
        MinSchoolsPerBand: params.minSchoolsPerBand,
        ExcludeDetails: params.excludeDetails,
        SkipCount: params.skipCount ?? 0,
        MaxResultCount: params.maxResultCount ?? 100,
        Sorting: params.sorting,
      })}`
    );
    return normalizeSchoolBandsPagedResult(raw);
  },

  /**
   * POST /api/app/school-bands/calc-band-school-count — recalculate numberOfSchools per band.
   */
  calcBandSchoolCount: async (items: CalcBandSchoolCountItem[]) => {
    const raw = await httpClient.post<unknown>(
      `${PREFIX}/calc-band-school-count`,
      toCalcBandSchoolCountPostBody(items)
    );
    return normalizeCalcBandSchoolCountResponse(raw);
  },

  /**
   * POST /api/app/school-bands/school-by-ids — school rows for given ids (year + min context).
   * Supports server paging (`skipCount`, `maxResultCount`), `name` filter, and `sorting`.
   */
  getSchoolsByIds: async (params: {
    year: number;
    minSchoolsPerBand: number;
    schoolIds: string[];
    name?: string;
    skipCount?: number;
    maxResultCount?: number;
    /** Server expects e.g. `TotalStudentCount` with `dir`. */
    sorting?: string;
    dir?: 'asc' | 'desc';
  }): Promise<SchoolByIdsPagedResult> => {
    const ids = params.schoolIds.filter((id) => id && id.trim() !== '');
    if (ids.length === 0) return { items: [], totalCount: 0 };

    const body = {
      name: params.name ?? '',
      year: params.year,
      minSchoolsPerBand: params.minSchoolsPerBand,
      schoolIds: ids,
      skipCount: params.skipCount ?? 0,
      maxResultCount: params.maxResultCount ?? 2147483647,
      sorting: params.sorting ?? 'TotalStudentCount',
      dir: params.dir ?? 'desc',
    };

    const raw = await httpClient.post<unknown>(`${PREFIX}/school-by-ids`, body);
    return normalizeSchoolByIdsResponse(raw);
  },
};
